/**
 * CloseAsLostDialog.tsx — why a deal was lost, as a value we can count.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Server: clinic-service
 * `src/deals/closed-reason.ts` and `patchStage`.
 *
 * ⚠️ WHY THIS IS ITS OWN DIALOG AND NOT `ConfirmDialog` WITH A SELECT BOLTED ON.
 * Two of its three jobs are things `ConfirmDialog` deliberately does not do:
 * it keeps itself OPEN when the server refuses, and it renders that refusal
 * INSIDE the panel. The shared confirm closes on submit and leaves the caller to
 * report failure — which is exactly how the old close-as-lost came to look
 * broken, see below.
 *
 * ── THE BUG THIS REPLACES (M, via Arham, 2026-09-17) ────────────────────────
 * "After clicking Close as Lost, the patient remains in Pending Admin Review."
 * No error, nothing. The server was refusing correctly, with a good sentence
 * naming the counts — and `runMove` closed the dialog and called `setNotice`,
 * which renders an Alert ON THE BOARD, behind this drawer's `position: fixed`
 * scrim and its `aria-modal="true"`. The message was produced, was correct, and
 * was invisible: covered for a sighted user, removed from the accessibility
 * tree for everyone else.
 *
 * That exact failure is already written up in `PipelineTab.tsx` for a DIFFERENT
 * action ("NOTICE=true, DIALOG STILL OPEN=true, NOTICE INSIDE DIALOG=false —
 * indistinguishable, to the person clicking, from the silent no-op"). It was
 * fixed there and left here. So: the dialog owns its own failure, because the
 * dialog owns the surface the user is looking at.
 */
import { useState } from 'react';
import { DialogShell } from '../ui/ConfirmDialog';
import { Alert, Button, ErrorLine, Field, Select, TextArea } from '../ui/kit';
import {
  CLOSED_REASON_OPTIONS,
  NO_CONTACT_REASON,
  REQUIRED_ROUND_ATTEMPTS,
  closeBlockedReason,
  reasonNeedsDetail,
  type ClosedReasonCode,
} from './closedReasons';

export function CloseAsLostDialog({
  dealName,
  attemptsThisRound,
  busy,
  serverError,
  onConfirm,
  onCancel,
}: {
  dealName: string;
  /** From the card. `null` when unknown — see `closeBlockedReason`; we do not guess. */
  attemptsThisRound: number | null;
  busy: boolean;
  /** The SERVER's own words on a refusal. Rendered verbatim; never re-derived. */
  serverError: string | null;
  onConfirm: (reasonCode: ClosedReasonCode, detail: string) => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState<ClosedReasonCode | ''>('');
  const [detail, setDetail] = useState('');

  const blocked = closeBlockedReason(code, detail, attemptsThisRound);
  const needsDetail = reasonNeedsDetail(code);
  // The attempts warning is shown the moment they PICK the reason, not on
  // submit: it is the one refusal the screen can see coming, and naming it while
  // the select is still open is what makes it advice rather than a rejection.
  const attemptsWarning =
    code === NO_CONTACT_REASON &&
    attemptsThisRound !== null &&
    attemptsThisRound < REQUIRED_ROUND_ATTEMPTS
      ? blocked
      : null;

  return (
    <DialogShell title={`Close ${dealName} as lost`} onCancel={onCancel}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gps-s-3)' }}>
        <Field label="Why are we closing this?" htmlFor="close-reason">
          <Select
            id="close-reason"
            value={code}
            disabled={busy}
            onChange={(e) => setCode(e.target.value as ClosedReasonCode | '')}
          >
            <option value="">Choose a reason…</option>
            {CLOSED_REASON_OPTIONS.map((option) => (
              <option key={option.code} value={option.code}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>

        {/* ⚠️ ADVICE, NOT A REFUSAL, and the tone says so. They have not done
            anything wrong yet — they have picked the one reason that claims we
            never reached this patient, on a round that says otherwise. The way
            out is in the sentence. */}
        {attemptsWarning ? (
          <Alert tone="warning" role="status">
            {attemptsWarning}
          </Alert>
        ) : null}

        <Field
          label={needsDetail ? 'What happened?' : 'Anything to add? (optional)'}
          htmlFor="close-detail"
        >
          <TextArea
            id="close-detail"
            rows={3}
            value={detail}
            disabled={busy}
            placeholder={needsDetail ? 'A short note — this one needs it' : 'Optional'}
            onChange={(e) => setDetail(e.target.value)}
          />
        </Field>

        {/* THE SERVER'S REFUSAL, IN THE PANEL. See the header: this is the whole
            reason the dialog is not a `ConfirmDialog`. */}
        {serverError ? (
          <div id="close-server-error">
            <ErrorLine>{serverError}</ErrorLine>
          </div>
        ) : null}

        <div style={{ display: 'flex', gap: 'var(--gps-s-2)', justifyContent: 'flex-end' }}>
          <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="cta"
            style={{ background: 'var(--gps-danger-strong)' }}
            // Disabled ONLY on what this screen can prove — a missing reason, a
            // missing required note, or an attempts count it actually holds.
            // Everything else is the server's to refuse, and it will be shown.
            disabled={busy || blocked !== null}
            aria-describedby={serverError ? 'close-server-error' : undefined}
            onClick={() => code !== '' && onConfirm(code, detail.trim())}
          >
            {busy ? 'Closing…' : 'Close as lost'}
          </Button>
        </div>
      </div>
    </DialogShell>
  );
}
